import { useState, useEffect, useCallback } from "react";
import { generateForensicInsights } from "@/lib/forensics";
import type { VerdictInputs, VerdictCalculations, VerdictFlags } from "@/types";

interface ThinkingBlockProps {
  inputs: VerdictInputs;
  calculations: VerdictCalculations;
  flags: VerdictFlags;
  isAnalyzing: boolean;
  onAnalysisComplete: () => void;
}

interface ThinkingStep {
  id: string;
  label: string;
  type: "process" | "finding";
  status: "pending" | "active" | "complete";
  metrics?: { label: string; value: string }[];
}

export function ThinkingBlock({
  inputs,
  calculations,
  flags,
  isAnalyzing,
  onAnalysisComplete,
}: ThinkingBlockProps) {
  // ── 3-phase intro sequence ──
  const [introPhase, setIntroPhase] = useState(0);
  const [introComplete, setIntroComplete] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setIntroPhase(1), 1000);
    const t2 = setTimeout(() => setIntroPhase(2), 2200);
    const t3 = setTimeout(() => setIntroComplete(true), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const [steps, setSteps] = useState<ThinkingStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Generate forensic insights to build steps
  const forensicInsights = generateForensicInsights(inputs, calculations, flags);

  // Build steps on mount
  useEffect(() => {
    const builtSteps: ThinkingStep[] = [
      { id: "floor", label: "Calculating floor", type: "process", status: "pending" },
    ];

    // Add forensic findings
    forensicInsights.forEach((insight) => {
      builtSteps.push({
        id: insight.key,
        label: insight.title,
        type: "finding",
        status: "pending",
        metrics: insight.metrics?.map((m) => ({ label: m.label, value: m.value })),
      });
    });

    // Add final step
    builtSteps.push({
      id: "analysis",
      label: "Generating analysis",
      type: "process",
      status: "pending",
    });

    setSteps(builtSteps);
  }, []);

  // Timer
  useEffect(() => {
    if (!isAnalyzing || isComplete) return;

    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 0.1);
    }, 100);

    return () => clearInterval(interval);
  }, [isAnalyzing, isComplete]);

  // Animate through steps
  useEffect(() => {
    if (!isAnalyzing || steps.length === 0 || isComplete) return;

    const stepDelay = steps.length > 4 ? 300 : 450;

    const timeout = setTimeout(() => {
      if (currentStepIndex < steps.length) {
        setSteps((prev) =>
          prev.map((step, idx) => {
            if (idx < currentStepIndex) return { ...step, status: "complete" };
            if (idx === currentStepIndex) return { ...step, status: "active" };
            return step;
          })
        );
        setCurrentStepIndex((prev) => prev + 1);
      } else {
        // All steps complete
        setSteps((prev) => prev.map((step) => ({ ...step, status: "complete" })));
        setIsComplete(true);

        // Auto-collapse after delay
        setTimeout(() => {
          setIsExpanded(false);
          onAnalysisComplete();
        }, 600);
      }
    }, currentStepIndex === 0 ? 100 : stepDelay);

    return () => clearTimeout(timeout);
  }, [isAnalyzing, currentStepIndex, steps.length, isComplete, onAnalysisComplete]);

  const issueCount = forensicInsights.length;
  const headerText = isComplete
    ? `Analyzed · Found ${issueCount} issue${issueCount !== 1 ? "s" : ""}`
    : "Analyzing your pricing...";

  const toggleExpanded = useCallback(() => {
    if (isComplete) {
      setIsExpanded((prev) => !prev);
    }
  }, [isComplete]);

  // ── Intro phase rendering ──
  if (!introComplete) {
    const INTRO_PHASES = [
      'Calculating floor price...',
      'Analyzing market corridor...',
      'Generating verdict...',
    ];
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '40px 20px', minHeight: 160,
      }}>
        <style>{`
          @keyframes tbDotPulse { 0%,100%{opacity:.3;transform:scale(.8)} 50%{opacity:1;transform:scale(1.2)} }
          @keyframes tbLineExpand { from{width:0} to{width:100%} }
          @keyframes tbCheckFade { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }
        `}</style>
        {introPhase === 0 && (
          <div style={{ display: 'flex', gap: 6 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: 4, background: '#3DABA5',
                animation: `tbDotPulse 1.2s ease-in-out ${i * 150}ms infinite`,
              }} />
            ))}
          </div>
        )}
        {introPhase === 1 && (
          <div style={{ width: 120, height: 2, background: '#E8E5DE', borderRadius: 1, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#3DABA5', borderRadius: 1, animation: 'tbLineExpand 1s cubic-bezier(0.4,0,0.2,1) forwards' }} />
          </div>
        )}
        {introPhase === 2 && (
          <div style={{
            width: 40, height: 40, borderRadius: 20, background: 'rgba(61,171,165,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'tbCheckFade 0.4s cubic-bezier(0.16,1,0.3,1) forwards',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#3DABA5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
        <div style={{
          fontSize: 14, fontWeight: 500, color: '#6B6B6B', marginTop: 12, textAlign: 'center',
          opacity: 1, transition: 'opacity 280ms ease',
        }}>
          {INTRO_PHASES[introPhase]}
        </div>
      </div>
    );
  }

  return (
     <div className="border border-charcoal-200 rounded-xl bg-cream-100 overflow-hidden">
      {/* Header - clickable when complete */}
      <button
        type="button"
        onClick={toggleExpanded}
        disabled={!isComplete}
        className={`w-full px-4 py-3 flex items-center justify-between text-left ${
          isComplete ? "cursor-pointer hover:bg-cream-100" : "cursor-default"
        } transition-colors`}
      >
        <span className="text-sm text-charcoal-700">{headerText}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-charcoal-400 tabular-nums">
            {elapsedTime.toFixed(1)}s
          </span>
          {isComplete && (
            <svg
              className={`w-4 h-4 text-charcoal-400 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {/* Expandable content */}
      {isExpanded && steps.length > 0 && (
        <div className="px-4 pb-4 border-t border-charcoal-100">
          <div className="pt-3">
            {steps.map((step, index) => (
              <div key={step.id} className="relative">
                {/* Vertical connector line */}
                {index < steps.length - 1 && (
                  <div
                    className={`absolute left-[7px] top-5 w-0.5 h-full ${
                      step.status === "complete" ? "bg-teal-300" : "bg-charcoal-200"
                    }`}
                  />
                )}

                {/* Step row */}
                <div className="flex items-start gap-3">
                  {/* Node indicator */}
                  <div
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5 ${
                      step.status === "complete"
                        ? "bg-teal-500 text-white"
                        : step.status === "active"
                        ? "bg-teal-500 text-white animate-pulse"
                        : "bg-charcoal-200 text-charcoal-400"
                    }`}
                  >
                    {step.status === "complete" ? "✓" : step.status === "active" ? "●" : ""}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-3">
                    <span className="text-sm text-charcoal-700">{step.label}</span>

                    {/* Metrics for findings */}
                    {step.type === "finding" && step.metrics && step.metrics.length > 0 && (
                      <div className="mt-0.5 flex flex-wrap gap-x-3">
                        {step.metrics.map((metric, idx) => (
                          <span key={idx} className="text-xs text-charcoal-400">
                            {metric.label}: {metric.value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ThinkingBlock;
